import { ConfirmDialog } from '@/components/confirm-dialog';
import { deleteFolderDescription } from '@/lib/folder-wording';
import { useDeleteFolder } from '@/lib/folders-queries';
import { useFolderNotes } from '@/lib/notes-queries';
import type { MatrixFolder } from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';

export const DELETE_FOLDER_ERROR_MESSAGE = 'La suppression a échoué. Réessayez.';

export interface DeleteFolderDialogProps {
  /** The folder to delete; `null` keeps the dialog closed. */
  folder: MatrixFolder | null;
  companyId: string;
  onClose: () => void;
  /** The folder is gone; the tab goes back to « Toutes les matrices ». */
  onDeleted: (folder: MatrixFolder) => void;
}

/**
 * Confirmation before deleting a folder (issue #19, planche 16): the matrices
 * go back « Hors dossier », the description and the notes are deleted. The
 * focus lands on « Annuler »; on failure the dialog stays open and the button
 * retries.
 */
export function DeleteFolderDialog({
  folder,
  companyId,
  onClose,
  onDeleted,
}: DeleteFolderDialogProps) {
  const mutation = useDeleteFolder(companyId);
  // The notes the caller can see, resolved ones included; the counter of the
  // column only has the open ones, which is the fallback while they load.
  const notes = useFolderNotes(folder?.id);
  const noteCount = notes.data ? notes.data.threads.length : (folder?.openNoteCount ?? 0);

  function cancel() {
    mutation.reset();
    onClose();
  }

  function confirm() {
    if (!folder) return;
    mutation.mutate(folder.id, {
      onSuccess: () => {
        mutation.reset();
        onDeleted(folder);
      },
    });
  }

  return (
    <ConfirmDialog
      open={folder !== null}
      title={folder ? `Supprimer le dossier « ${folder.name} » ?` : ''}
      description={folder ? deleteFolderDescription(folder.matrixCount, noteCount) : ''}
      confirmLabel="Supprimer le dossier"
      pendingLabel="Suppression…"
      confirmIcon={<Icon name="delete" size={16} />}
      confirmVariant="destructive"
      isPending={mutation.isPending}
      errorMessage={mutation.isError ? DELETE_FOLDER_ERROR_MESSAGE : null}
      onConfirm={confirm}
      onCancel={cancel}
    />
  );
}
